/**
 * El paso 1: soltar el archivo.
 *
 * EL PROVEEDOR YA NO SE ELIGE ANTES (0150)
 * ----------------------------------------
 * Antes el selector iba arriba del archivo y arrancaba en «Sin proveedor»: 5
 * de las 7 listas de produccion se subieron asi y todo se emparejo por nombre.
 * Ahora la lectura dice de que proveedor es y la pantalla de la lista lo
 * muestra arriba; si el archivo no lo dice, ahi se elige con un toque, y no
 * se puede actualizar sin contestarlo.
 */
import { useRef, useState, type DragEvent } from "react";
import { FileUp, Paperclip, TriangleAlert, X } from "lucide-react";

import { Aviso } from "@/components/ui/aviso";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { IconButton } from "@/components/ui/icon-button";
import { Label } from "@/components/ui/label";
import { Progreso } from "@/components/ui/progreso";
import { cn } from "@/lib/utils";

import { EXTENSIONES_ACEPTADAS, formatearBytes, validarArchivo } from "./logica";

export interface SubidaListaProps {
  subiendo: boolean;
  /** Porcentaje de bytes enviados. Solo se muestra mientras `subiendo`. */
  progreso: number;
  onSubir: (archivo: File, proveedorId: number | null) => void;
}

export function SubidaLista({ subiendo, progreso, onSubir }: SubidaListaProps): JSX.Element {
  const [archivo, setArchivo] = useState<File | null>(null);
  const [problema, setProblema] = useState<string | null>(null);
  const [encima, setEncima] = useState(false);
  const input = useRef<HTMLInputElement>(null);

  function tomar(elegido: File | null | undefined): void {
    if (!elegido) return;
    const validacion = validarArchivo(elegido);
    if (!validacion.valida) {
      setArchivo(null);
      setProblema(validacion.motivo ?? "No podemos leer ese archivo.");
      return;
    }
    setProblema(null);
    setArchivo(elegido);
  }

  function soltar(evento: DragEvent<HTMLDivElement>): void {
    evento.preventDefault();
    setEncima(false);
    tomar(evento.dataTransfer.files.item(0));
  }

  function enviar(): void {
    if (archivo === null || subiendo) return;
    onSubir(archivo, null);
  }

  return (
    <Card padding="normal" className="space-y-4 sm:p-6">
      <div className="space-y-1.5">
        <Label htmlFor="archivo-lista">Archivo de la lista</Label>
        <Card
          variant="dashed"
          onDragOver={(e) => {
            e.preventDefault();
            setEncima(true);
          }}
          onDragLeave={() => setEncima(false)}
          onDrop={soltar}
          className={cn(
            "flex flex-col items-center gap-3 p-6 text-center transition-colors duration-150",
            encima && "border-ring bg-accent",
          )}
        >
          <FileUp className="size-6 text-muted-foreground" aria-hidden="true" />
          <div className="space-y-1">
            <p className="text-sm font-medium">Arrastrá el PDF, el Excel o la foto acá</p>
            <p className="text-xs text-muted-foreground">
              PDF, Excel, CSV, texto o imagen. Hasta {formatearBytes(24 * 1024 * 1024)}.
            </p>
          </div>
          {/* <input type="file"> crudo: no hay primitivo para elegir archivos; queda
              oculto y lo dispara el boton. */}
          <input
            ref={input}
            id="archivo-lista"
            type="file"
            className="sr-only"
            accept={EXTENSIONES_ACEPTADAS.join(",")}
            disabled={subiendo}
            onChange={(e) => tomar(e.target.files?.item(0))}
          />
          <Button
            type="button"
            variant="outline"
            size="sm"
            disabled={subiendo}
            onClick={() => input.current?.click()}
          >
            Buscar en la computadora
          </Button>
        </Card>
      </div>

      {problema ? (
        <Aviso tono="destructive" icono={TriangleAlert} compacto>
          {problema}
        </Aviso>
      ) : null}

      {archivo ? (
        <Card
          variant="muted"
          padding="compacto"
          className="flex items-center justify-between gap-3"
        >
          <span className="flex min-w-0 items-center gap-2">
            <Paperclip className="size-4 shrink-0 text-muted-foreground" aria-hidden="true" />
            <span className="min-w-0">
              <span className="block truncate text-sm font-medium">{archivo.name}</span>
              <span className="block text-xs tabular-nums text-muted-foreground">
                {formatearBytes(archivo.size)}
              </span>
            </span>
          </span>
          {!subiendo ? (
            <IconButton
              icono={X}
              tamano="sm"
              onClick={() => setArchivo(null)}
              aria-label="Sacar el archivo"
              className="shrink-0 text-muted-foreground"
            />
          ) : null}
        </Card>
      ) : null}

      {subiendo ? (
        <div className="space-y-1.5">
          <div className="flex items-baseline justify-between text-xs">
            <span className="text-muted-foreground">Subiendo</span>
            <span className="tabular-nums">{progreso} %</span>
          </div>
          <Progreso valor={progreso} aria-label="Subida del archivo" />
        </div>
      ) : null}

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
        <p className="text-xs text-muted-foreground">
          Subir no cambia ningún precio. Después vas a poder revisar fila por fila.
        </p>
        <Button type="button" onClick={enviar} disabled={archivo === null} cargando={subiendo}>
          Leer la lista
        </Button>
      </div>
    </Card>
  );
}
